import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BcmService } from '@modules/bcm/bcm.service';
import { BcmBaseFacade } from '../bcm-base.facade';
import { ConfirmDialogService } from '@sharedComponents/dialogs/confirm-dialog/confirm-dialog.service';
import { BcmUserPermission } from '@modules/bcm/bcm-user-permission';
import {
    filteredListExportHeaderActionBase,
    predefinedReportsExcelHeaderActionBase,
} from '@modules/bcm/@core/state-management/filtered-list-export-header.util';
import { BcmExportExcelApiService } from '@bcmApiServices/bcm-export-excel.api-service';
import { AppNotificationService } from '@core/services/app-notification.service';
import { BcmNavigationService } from '@modules/bcm/bcm-navigation.service';
import { Berth, IBerth } from '@shared/models/berth';
import { berthsTableData } from '@modules/bcm/@core/state-management/berths/berths-table-data';
import { ThemePalette } from '@angular/material/core';
import { BcmUserSettingsFacade } from '@bcmServices/settings/bcm-user-settings-facade';
import { BcmBaseBulkChanges } from '@modules/bcm/@core/state-management/bcm-base-bulk-changes';
import { BcmFinancialRecord } from '@shared/models/bcm-financial-record';
import { Router } from '@angular/router';
import { WorkFlowService } from '@bcm-work-flows/work-flow.service';
import { BookingType } from '@modules/bcm/@shared/enums/berth-reservation-type';
import { take, tap } from 'rxjs/operators';
import { Observable } from 'rxjs';
import { MatDialog } from '@angular/material/dialog';
import { BcmTenantPermission } from '@modules/bcm/bcm-tenant-permission';
import { TableRowAction } from '@modules/bcm/@core/state-management/bcm-base.types';
import { BerthOnlineBookingQrDialogComponent } from '@modules/bcm/berths/berth-online-booking-qr-dialog/berth-online-booking-qr-dialog.component';
import { TranslationService } from '@core/translation/translation.service';

@Injectable({providedIn: 'root'})
export class BerthsFacade extends BcmBaseFacade<Berth, IBerth> {

    /** Set on Liegeplatz → Produkte while the tab is active; persists drag-and-drop order on save. */
    productSortOrderSaveCallback: (() => Observable<unknown>) | null = null;

    readonly resourcePath = 'berths';

    protected override get listResourcePath(): string {
        return 'berths/index';
    }

    get resourceNameSingular(): string {
        return this._tolgee.translate('bcmBerths.singular');
    }

    get resourceNamePlural(): string {
        return this._tolgee.translate('bcmBerths.plural');
    }

    readonly resourceIconPlural = 'directions_boat';

    readonly resourceIconSingular = 'directions_boat';

    readonly deletePermission = BcmUserPermission.BERTHS_DELETE;

    readonly readPermission = BcmUserPermission.BERTHS_READ;

    readonly writePermission = BcmUserPermission.BERTHS_WRITE;

    readonly hideGenericExcelButton = true;

    readonly headerActions = [
        {
            titleKey: 'bcmBerths.showMapView',
            onClick: () => this._bcmNavigationService.navigate(['berths', 'map']),
            icon: 'compare_arrows',
            showDividerBelow: true
        },
        {
            titleKey: 'table.exportCurrentView',
            icon: 'table_view',
            onClick: () => this.exportTableAsExcel(),
            ...filteredListExportHeaderActionBase(),
        },
        {
            titleKey: 'table.excelExportBerthOccupancy',
            onClick: () => this._bcmExportExcelApiService.exportBerthsAssignments(),
            ...predefinedReportsExcelHeaderActionBase(),
        }
    ];

    readonly rowActions: TableRowAction<Berth>[] = [
        {
            titleKey: 'table.rowActionView',
            icon: 'assignment',
            iconColor: 'accent' as ThemePalette,
            onClick: (berth: Berth) => this._bcmNavigationService.navigate('/berths/' + berth.id)
        },
        {
            titleKey: 'bcmBerths.onlineBookingQr.rowAction',
            icon: 'qr_code_2',
            iconColor: 'accent' as ThemePalette,
            showButton: () => this._bcmTenantService.checkPermission(BcmTenantPermission.ONLINE_BERTH_BOOKING),
            onClick: (berth: Berth) => this.openOnlineBookingQrDialog(berth),
        },
        {
            titleKey: 'table.rowActionDelete',
            icon: 'delete',
            iconColor: 'warn' as ThemePalette,
            onClick: (berth: Berth) =>  super.remove(berth, berth.handle, 'berths/list').subscribe()
        }
    ];

    readonly bulkChanges = new BcmBaseBulkChanges<Berth>()
        .setButtons(
            {
                titleKey: 'berths.locks.newBooking',
                icon: 'add',
                iconColor: 'accent' as ThemePalette,
                resetSelectionAfterSuccess: true,
                onClick: async (berths: Berth[]) => {
                    await this.createBookingForBerths(berths, BookingType.Booking);
                }
            },
            {
                titleKey: 'berths.locks.newReservation',
                icon: 'add',
                iconColor: 'accent' as ThemePalette,
                resetSelectionAfterSuccess: true,
                onClick: async (berths: Berth[]) => {
                    await this.createBookingForBerths(berths, BookingType.Reservation);
                }
            },
            {
                titleKey: 'common.delete',
                icon: 'delete',
                iconColor: 'warn' as ThemePalette,
                resetSelectionAfterSuccess: true,
                onClick: (berths: Berth[]) => super.removeMultiple(berths),
            },
        );

    constructor(appNotificationService: AppNotificationService,
                confirmDialogService: ConfirmDialogService,
                bcmUserSettingsFacade: BcmUserSettingsFacade,
                httpClient: HttpClient,
                bcmService: BcmService,
                bcmNavigationService: BcmNavigationService,
                private workFlowService: WorkFlowService,
                private _bcmExportExcelApiService: BcmExportExcelApiService,
                private _bcmNavigationService: BcmNavigationService,
                private _matDialog: MatDialog,
    ) {
        super(
            Berth,
            berthsTableData,
            bcmNavigationService,
            appNotificationService,
            confirmDialogService,
            bcmUserSettingsFacade,
            httpClient,
            bcmService
        );

        this.rowClickHandler = (berth: Berth) => {
            this._bcmNavigationService.navigate('/berths/' + berth.id);
        };
    }

    openOnlineBookingQrDialog(berth: Berth): void {
        this._matDialog.open(BerthOnlineBookingQrDialogComponent, {
            width: '520px',
            data: {
                berthId: berth.id,
                berthHandle: berth.handle,
            },
        });
    }

    public override saveLastUnsavedFormState() {
        if (!this.hasRegisteredFormGroup) {
            throw Error('<facade>.saveLastUnsavedFormState() can only be called if a formGroup was registered.');
        }
        if (!this._userService.checkPermission(this.writePermission)) {
            this.appNotificationService.showError(TranslationService.translate('common.error.noSavePermission', { permission: this.resourceNamePluralDisplay }));
            return;
        }
        const formGroup = this.formGroup;
        if (formGroup.invalid) {
            formGroup.markAllAsTouched();
            this.appNotificationService.showError(TranslationService.translate('common.checkFieldsHighlight'));
            return;
        }
        if (this.productSortOrderSaveCallback && formGroup.dirty) {
            return this.productSortOrderSaveCallback()
                .pipe(
                    tap(() => {
                        formGroup.markAsPristine();
                        this.appNotificationService.showSuccess(TranslationService.translate('bcmBerths.notifications.dataSaved'));
                    }),
                )
                .subscribe();
        }
        return super.saveLastUnsavedFormState();
    }

    private async createBookingForBerths(berths: Berth[], bookingType: BookingType): Promise<void> {
        if (!berths || berths.length === 0) {
            return;
        }

        const addAssignmentObservable = await this.workFlowService.addBerthAssignment(berths, bookingType);
        
        if (addAssignmentObservable) {
            addAssignmentObservable
                .pipe(take(1))
                .subscribe((result) => {
                    if (result) {
                        this.reloadCurrentListOrPage();
                    }
                });
        }
    }
}
